import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Box, Typography, IconButton, Chip, Autocomplete,
  CircularProgress, FormControl, InputLabel, Select, MenuItem, Paper,
  List, ListItemButton, ListItemText, Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import { orderService, CreateWmsOrderDto, CreateWmsOrderItemDto } from '../services/orderService';
import { WMS_TYPE_CHIP_SX } from '../shared/wmsTypeChip';
import GuideButton from '../../common/guide/GuideButton';

interface ProductOption {
  id: string;
  name: string;
  barcode: string;
  stockCode: string;
  categoryId?: string;
  categoryName?: string;
}

interface CategoryOption {
  id: string;
  name: string;
}

interface OrderItemRow {
  product: ProductOption | null;
  quantity: number;
}

type WmsManualOrderSource = 'ecommerce' | 'trendyol' | 'hepsiburada' | 'n11' | 'pazarama' | 'pttavm';

interface CreateOrderModalProps {
  open: boolean;
  warehouseId: string;
  initialSource?: WmsManualOrderSource;
  onClose: () => void;
  onCreated: (created?: { sourceType: string; orderNumber?: string; orderId?: string }) => void;
}

const PRIORITY_OPTIONS = [
  { value: 0, label: 'Normal' },
  { value: 1, label: 'Yüksek' },
  { value: 2, label: 'Acil' },
];

const SOURCE_OPTIONS = [
  { value: 'ecommerce', label: 'E-Ticaret' },
  { value: 'trendyol', label: 'Trendyol' },
  { value: 'hepsiburada', label: 'Hepsiburada' },
  { value: 'n11', label: 'N11' },
  { value: 'pazarama', label: 'Pazarama' },
  { value: 'pttavm', label: 'PttAVM' },
] as const;

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  open,
  warehouseId,
  initialSource = 'ecommerce',
  onClose,
  onCreated,
}) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [shippingMethod, setShippingMethod] = useState('');
  const [sourceType, setSourceType] = useState<WmsManualOrderSource>(initialSource);
  const [priority, setPriority] = useState(0);
  const [items, setItems] = useState<OrderItemRow[]>([{ product: null, quantity: 1 }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryProductsById, setCategoryProductsById] = useState<Record<string, ProductOption[]>>({});
  const [activeCategoryByRow, setActiveCategoryByRow] = useState<Record<number, string | null>>({});
  const [searchTextByRow, setSearchTextByRow] = useState<Record<number, string>>({});
  const [activeSearchRow, setActiveSearchRow] = useState<number | null>(null);

  useEffect(() => {
    if (open) setSourceType(initialSource);
  }, [open, initialSource]);

  const mapProducts = useCallback((data: any[]): ProductOption[] => (
    (Array.isArray(data) ? data : []).map((p: any) => ({
      id: p.id || p.Id,
      name: p.name || p.Name || p.itemName || p.ItemName || '',
      barcode: p.barcode || p.Barcode || '',
      stockCode: p.stockCode || p.StockCode || '',
      categoryId: p.categoryId || p.CategoryId || undefined,
      categoryName: p.categoryName || p.CategoryName || undefined,
    }))
  ), []);

  useEffect(() => {
    if (!open) return;
    setCategoriesLoading(true);
    accountingApi
      .get(ACCOUNTING_API.INVENTORY_CATEGORIES)
      .then((res) => {
        const data = res.data?.data || res.data || [];
        const mapped = (Array.isArray(data) ? data : []).map((c: any) => ({
          id: c.id || c.Id,
          name: c.name || c.Name || 'Kategori',
        }));
        setCategories(mapped);
      })
      .catch(() => setCategories([]))
      .finally(() => setCategoriesLoading(false));
  }, [open]);

  const searchProducts = useCallback(async (query: string) => {
    if (query.length < 2) { setProductOptions([]); return; }
    setProductLoading(true);
    try {
      const res = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, {
        PageNumber: 1,
        PageSize: 20,
        SearchTerm: query,
        IsActive: true,
        HideZeroStock: false,
      });
      let data =
        res.data?.items ||
        res.data?.Items ||
        res.data?.data?.items ||
        res.data?.data?.Items ||
        res.data?.data ||
        [];

      // Bazi tenant ortamlarda paged endpoint aramada bos donebiliyor.
      // Bu durumda standart items endpointinden cekip istemci tarafinda filtrele.
      if (!Array.isArray(data) || data.length === 0) {
        const fallback = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEMS);
        const allItems = Array.isArray(fallback.data)
          ? fallback.data
          : (fallback.data?.data || fallback.data?.items || fallback.data?.Items || []);
        const q = query.trim().toLowerCase();
        data = (Array.isArray(allItems) ? allItems : []).filter((p: any) => {
          const name = String(p.name || p.Name || p.itemName || p.ItemName || '').toLowerCase();
          const barcode = String(p.barcode || p.Barcode || '').toLowerCase();
          const stockCode = String(p.stockCode || p.StockCode || '').toLowerCase();
          return name.includes(q) || barcode.includes(q) || stockCode.includes(q);
        }).slice(0, 20);
      }

      setProductOptions(mapProducts(data));
    } catch { setProductOptions([]); }
    setProductLoading(false);
  }, [mapProducts]);

  const loadCategoryProducts = useCallback(async (categoryId: string): Promise<ProductOption[]> => {
    if (categoryProductsById[categoryId]) return categoryProductsById[categoryId];
    try {
      const paged = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, {
        PageNumber: 1,
        PageSize: 100,
        CategoryId: categoryId,
        IsActive: true,
        HideZeroStock: false,
      });
      let data =
        paged.data?.items ||
        paged.data?.Items ||
        paged.data?.data?.items ||
        paged.data?.data?.Items ||
        paged.data?.data ||
        [];

      if (!Array.isArray(data) || data.length === 0) {
        const fallback = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEMS);
        const allItems = Array.isArray(fallback.data)
          ? fallback.data
          : (fallback.data?.data || fallback.data?.items || fallback.data?.Items || []);
        data = (Array.isArray(allItems) ? allItems : []).filter((p: any) => {
          const pCategory = p.categoryId || p.CategoryId;
          return pCategory === categoryId;
        });
      }

      const mapped = mapProducts(data);
      setCategoryProductsById((prev) => ({ ...prev, [categoryId]: mapped }));
      return mapped;
    } catch {
      setCategoryProductsById((prev) => ({ ...prev, [categoryId]: [] }));
      return [];
    }
  }, [categoryProductsById, mapProducts]);

  const handleAddItem = () => setItems(prev => [...prev, { product: null, quantity: 1 }]);

  const handleRemoveItem = (idx: number) => setItems(prev => prev.filter((_r, i) => i !== idx));

  const handleItemChange = (idx: number, field: 'product' | 'quantity', value: any) => {
    setItems(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleCategorySelect = async (rowIndex: number, categoryId: string) => {
    setActiveCategoryByRow((prev) => ({ ...prev, [rowIndex]: categoryId }));
    const products = await loadCategoryProducts(categoryId);
    setProductOptions(products);
  };

  const totalQuantity = items.reduce((s, r) => s + (r.product ? r.quantity : 0), 0);
  const validItems = items.filter(r => r.product);
  const orderType = totalQuantity === 1 ? 'Tekli' : 'Çoklu';

  const handleSave = async () => {
    if (!customerName.trim()) { setError('Müşteri adı zorunludur'); return; }
    if (validItems.length === 0) { setError('En az bir ürün eklenmelidir'); return; }
    setError('');
    setSaving(true);
    try {
      const orderItems: CreateWmsOrderItemDto[] = validItems.map(r => ({
        ProductId: r.product!.id,
        Quantity: r.quantity,
        Barcode: r.product!.barcode || undefined,
        SKU: r.product!.stockCode || undefined,
      }));
      const dto: CreateWmsOrderDto = {
        WarehouseId: warehouseId,
        OrderNumber: orderNumber || undefined,
        // Entegrasyon geldiginde backend kaynagi bu alanla dogrudan kaydetmeye devam edecek.
        SourceType: sourceType,
        CustomerName: customerName,
        CustomerPhone: customerPhone || undefined,
        CustomerAddress: customerAddress || undefined,
        ShippingMethod: shippingMethod || undefined,
        Priority: priority,
        Items: orderItems,
      };
      const res = await orderService.create(dto);
      const created = res?.data?.data || res?.data || {};
      onCreated({
        sourceType,
        orderId: created?.id || created?.Id,
        orderNumber: created?.orderNumber || created?.OrderNumber || orderNumber || undefined,
      });
      resetForm();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Sipariş oluşturulurken hata oluştu');
    }
    setSaving(false);
  };

  const resetForm = () => {
    setOrderNumber(''); setCustomerName(''); setCustomerPhone('');
    setCustomerAddress(''); setShippingMethod(''); setPriority(0);
    setSourceType(initialSource);
    setItems([{ product: null, quantity: 1 }]);
    setCategoryProductsById({});
    setActiveCategoryByRow({});
    setSearchTextByRow({});
    setActiveSearchRow(null);
    setError('');
  };

  const handleClose = () => { resetForm(); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        Manuel Sipariş Ekle
        <GuideButton guideKey="wms.createOrder" size="small" />
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField size="small" label="Sipariş No (opsiyonel)" value={orderNumber}
            onChange={e => setOrderNumber(e.target.value)} placeholder="Boş bırakılırsa otomatik" />
          <TextField size="small" label="Müşteri Adı *" value={customerName}
            onChange={e => setCustomerName(e.target.value)} />
          <TextField size="small" label="Telefon" value={customerPhone}
            onChange={e => setCustomerPhone(e.target.value)} />
          <TextField size="small" label="Adres" value={customerAddress}
            onChange={e => setCustomerAddress(e.target.value)} multiline rows={2} />
          <TextField size="small" label="Kargo Yöntemi" value={shippingMethod}
            onChange={e => setShippingMethod(e.target.value)} />
          <FormControl size="small">
            <InputLabel>Sipariş Kaynağı</InputLabel>
            <Select value={sourceType} label="Sipariş Kaynağı" onChange={e => setSourceType(e.target.value as any)}>
              {SOURCE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Öncelik</InputLabel>
            <Select value={priority} label="Öncelik" onChange={e => setPriority(Number(e.target.value))}>
              {PRIORITY_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Ürünler</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddItem}>Ürün Ekle</Button>
            </Box>
            {items.map((row, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {(() => {
                    const rowSearchText = (searchTextByRow[idx] || '').trim();
                    const shouldOpenSearchDropdown = rowSearchText.length >= 2;
                    return (
                  <Autocomplete size="small" sx={{ flex: 1 }}
                    open={shouldOpenSearchDropdown && activeSearchRow === idx}
                    onOpen={() => setActiveSearchRow(idx)}
                    onClose={() => setActiveSearchRow((prev) => (prev === idx ? null : prev))}
                    options={productOptions} loading={productLoading}
                    getOptionLabel={(o) => `${o.name} ${o.barcode ? `(${o.barcode})` : ''}`}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={row.product} onChange={(_e, v) => {
                      handleItemChange(idx, 'product', v);
                      if (v) {
                        setSearchTextByRow((prev) => ({ ...prev, [idx]: '' }));
                        setActiveSearchRow(null);
                      }
                    }}
                    onInputChange={(_e, v, reason) => {
                      if (reason === 'reset') return;
                      setSearchTextByRow((prev) => ({ ...prev, [idx]: v }));
                      if (v.trim().length >= 2) {
                        searchProducts(v.trim());
                        setActiveSearchRow(idx);
                      } else {
                        setProductOptions([]);
                      }
                    }}
                    noOptionsText="Sonuç bulunamadı"
                    loadingText="Yükleniyor..."
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ürün"
                        onFocus={() => setActiveSearchRow(idx)}
                        InputProps={{ ...params.InputProps, endAdornment: (
                          <>{productLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>
                        )}}
                      />
                    )}
                  />
                    );
                  })()}
                  <TextField size="small" type="number" label="Adet" value={row.quantity}
                    onChange={e => handleItemChange(idx, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                    sx={{ width: 80 }} inputProps={{ min: 1 }} />
                  {items.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => handleRemoveItem(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {!row.product && !(searchTextByRow[idx] || '').trim() && (
                  <Paper variant="outlined" sx={{ mt: 0.75, p: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Kategoriler (kategoriye tıklayınca ürünler açılır)
                    </Typography>
                    {categoriesLoading ? (
                      <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={16} />
                      </Box>
                    ) : (
                      <List dense disablePadding sx={{ maxHeight: 220, overflowY: 'auto' }}>
                        {categories.map((category) => {
                          const active = activeCategoryByRow[idx] === category.id;
                          const products = categoryProductsById[category.id] || [];
                          return (
                            <Box key={category.id}>
                              <ListItemButton
                                dense
                                onClick={() => handleCategorySelect(idx, category.id)}
                                sx={{
                                  borderRadius: 1,
                                  mb: 0.25,
                                  bgcolor: active ? 'action.selected' : 'transparent',
                                }}
                              >
                                {active ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                <ListItemText
                                  primary={category.name}
                                  primaryTypographyProps={{ variant: 'body2', fontWeight: active ? 700 : 500 }}
                                />
                              </ListItemButton>

                              <Collapse in={active} timeout="auto" unmountOnExit>
                                <Box sx={{ pl: 3.5, pr: 1, pb: 0.5 }}>
                                  {products.slice(0, 24).map((product) => (
                                    <Box
                                      key={product.id}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => handleItemChange(idx, 'product', product)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          handleItemChange(idx, 'product', product);
                                        }
                                      }}
                                      sx={{
                                        py: 0.4,
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        borderRadius: 1,
                                        color: 'text.secondary',
                                        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                                      }}
                                    >
                                      {`- ${product.name}`}
                                    </Box>
                                  ))}
                                  {products.length === 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Bu kategoride ürün bulunamadı.
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </List>
                    )}
                  </Paper>
                )}

                {!row.product && !!(searchTextByRow[idx] || '').trim() && (searchTextByRow[idx] || '').trim().length < 2 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Arama için en az 2 karakter yazın veya aşağıdan kategoriden seçin.
                  </Typography>
                )}
              </Box>
            ))}
            {validItems.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Tip: <Chip label={orderType} size="small" variant="outlined"
                  color={totalQuantity === 1 ? 'info' : 'secondary'} sx={{ ...WMS_TYPE_CHIP_SX, height: 20, fontSize: '0.65rem' }} />
                {' '}({totalQuantity} ürün)
              </Typography>
            )}
          </Box>

          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>İptal</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Sipariş Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrderModal;
